import { createContext, useEffect, useContext, useCallback, useReducer, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthContext } from '../auth/useAuthContext';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';

// ----------------------------------------------------------------------

const initialState = {
  eventTags: [],
  error: false,
  page:0,
  totalPages :1,
  recordPerPage:15,
  total_records:null
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      eventTags: action.payload.eventTags,
      page:action.payload.page,
      totalPages: action.payload.totalPages,
      recordPerPage:action.payload.recordPerPage,
      total_records: action.payload.total_records
    };
  }
  if (action.type === 'ERROR') {
    return {
      ...state,
      error: true,
    };
  }
  if (action.type === 'CREATE_TAG') {
    return {
      ...state,
      eventTags: [...state.eventTags, action.payload.eventTag],
    };
  }
  if (action.type === 'UPDATE_TAG') {
    const tags = [...state.eventTags];
    const index = tags.findIndex((tag) => tag._id === action.payload.eventTag._id);
    tags[index] = action.payload.eventTag;
    return {
      ...state,
      eventTags: tags,
    };
  }
  if (action.type === 'PAGE_CHANGE') {
    return {
      ...state,
      page: action.payload.page,
    };
  }
  if(action.type === 'PAGE_SIZE_CHANGE'){
   return{
    ...state,
    recordPerPage:action.payload.recordPerPage,
    page:0
   }
  }
  return state;
};

// ----------------------------------------------------------------------

export const EventTagsContext = createContext(initialState);

export const useEventTagsContext = () => {
  const context = useContext(EventTagsContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

export function EventTagsProvider() {
  const { user, logout } = useAuthContext();
  const storageAvailable = localStorageAvailable();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getEventTags = useCallback(async (page,page_size,vendor) => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: '/service/events_service/v1/events/tags',
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        params:{
          page,
          page_size,
          vendor
        }
      };
      

      const response = await axios.request(config);
      console.log(response,'res')
      const { tags, currentPage, totalPages,recordPerPage,total_records} = response.data;
      console.log(response.data,'-------res-------')
      dispatch({
        type: 'INITIAL',
        payload: {
          eventTags: tags,
          page:currentPage,
          totalPages,
          recordPerPage:page_size,
          total_records
        },
      });
    } catch (error) {
      if (error === 'Unauthorized request') {
        logout();
      }
      console.log(error);
      dispatch({
        type: 'ERROR',
      });
    }
  }, [user, storageAvailable, logout]);

  useEffect(() => {
  getEventTags(state.page, state.recordPerPage);
}, [getEventTags, state.page, state.recordPerPage]);


 const pageChange = useCallback(
  (page) => {
    getEventTags(page, state.recordPerPage);
  },
  [getEventTags, state.recordPerPage]
);

 const setPage = useCallback(
  (updater) => {
    
    const newPage = typeof updater === "function" ? updater(state.page) : updater;

    dispatch({ type: 'PAGE_CHANGE', payload: { page: newPage } });
    getEventTags(newPage, state.recordPerPage);
  },
  [getEventTags, state.recordPerPage, state.page]
);

const setRecordPerPage = useCallback(
  (recordPerPage) => {
    dispatch({ type: 'PAGE_SIZE_CHANGE', payload: { recordPerPage } });
    getEventTags(0, recordPerPage); 
  },
  [getEventTags]
);


  const createTag = useCallback(
    async (data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: '/service/events_service/v1/events/tags',
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      const response = await axios.request(config);

      dispatch({
        type: 'CREATE_TAG',
        payload: {
          eventTag: response.data,
        },
      });
      return response;
    },
    [storageAvailable, user]
  );

  const updateTag = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/service/events_service/v1/events/tags`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
        params: { id },
      };
      const response = await axios.request(config);
      dispatch({
        type: 'UPDATE_TAG',
        payload: {
          eventTag: response.data,
        },
      });
      return response;
    },
    [storageAvailable, user]
  );

  const memoizedValue = useMemo(
    () => ({ eventTags: state.eventTags,page: state.page, totalPages: state.totalPages,recordPerPage:state.recordPerPage,total_records:state.total_records,pageChange, createTag, updateTag,setPage,setRecordPerPage }),
    [createTag, state.eventTags, state.page, state.totalPages,state.recordPerPage,state.total_records,pageChange, updateTag]
  );

  return (
    <EventTagsContext.Provider value={memoizedValue}>
      <Outlet />
    </EventTagsContext.Provider>
  );
}
